import { useGetBreeds, useLog } from '@application/api';
import type { Language } from '@application/types';
import { scrollToError, useRouter, useWindowConfirmUnload } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import type { MtBreederOnboardingFormValues, OnboardingStep } from '@presentation/pages';
import {
  Step0,
  Step1,
  Step2,
  Step3,
  Step4,
  Step5,
  Step6,
  Step7,
  Step8,
  Step9,
  Step10,
  Step11,
  Step12,
  Step13,
  MtBreederOnboardingPage,
} from '@presentation/pages';
import { FormikProvider, useFormik } from 'formik';
import hash from 'object-hash';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BREEDER_ONBOARDING_MAX_STEP } from '@application/constants';
import { useCurrentProfileStep } from './api/useCurrentProfileStep';
import { useGetProfile } from './api/useGetProfile';
import { useProfileInitialValuesBuilders } from './api/useProfileInitialValuesBuilder';
import { useUpdateProfile } from './api/useUpdateProfile';
import { useValidationSchema } from './useValidationSchema';
import type { MtBreederOnboardingPageControllerProps } from './types';

export const MtBreederOnboardingPageController: FC<MtBreederOnboardingPageControllerProps> = (
  props,
) => {
  const { action } = props;
  const { navigateToRoute, language, translate } = useRouter();
  const { breedOptionsWithoutCrossbreeds } = useGetBreeds();
  const [currentStep, setStep] = useState(action === 'edit' ? BREEDER_ONBOARDING_MAX_STEP : 0);
  const { currentProfileStep } = useCurrentProfileStep();
  const { profile } = useGetProfile();

  const { log } = useLog();
  const { updateProfileFromForm, updateProfileLoading, finishOnboarding } =
    useUpdateProfile(profile);
  const { onboardingFormValidationSchemas } = useValidationSchema();

  const translations = useMemo((): Language[] => {
    switch (language) {
      case 'nl':
        return ['nl', 'de', 'fr'];
      case 'fr':
        return ['fr', 'nl', 'de'];
      default:
        return ['de', 'nl', 'fr'];
    }
  }, [language]);

  const { profileInitialValues } = useProfileInitialValuesBuilders(translations, profile);

  const validationSchema = useMemo(() => {
    return onboardingFormValidationSchemas[currentStep];
  }, [onboardingFormValidationSchemas, currentStep]);

  const onEditClickHandler = () => {
    navigateToRoute('mtBreederProfile');
  };

  const onStartClickHandler = useCallback(() => {
    setStep(currentProfileStep);
  }, [currentProfileStep]);

  const formSubmitHandler = useCallback(
    async (values: MtBreederOnboardingFormValues) => {
      const changed = hash(values) !== hash(profileInitialValues);

      if (changed) {
        await updateProfileFromForm(values);
      }

      if (currentStep > 12) {
        navigateToRoute('mtLanding');
      } else {
        let nextStep = currentStep + 1;
        // Skip step 2 for swiss users
        if (values.country === CountryCode.CH && currentStep === 1) {
          nextStep += 1;
        }
        if (nextStep === BREEDER_ONBOARDING_MAX_STEP) {
          await finishOnboarding();
        }
        setStep(nextStep);
      }
    },
    [profileInitialValues, currentStep, updateProfileFromForm, finishOnboarding, navigateToRoute],
  );

  const form = useFormik<MtBreederOnboardingFormValues>({
    initialValues: profileInitialValues,
    validationSchema,
    onSubmit: formSubmitHandler,
    enableReinitialize: true,
  });

  const breederTypeWarning = useMemo(() => {
    return translate(`mt.breeder.onboarding.breederType.warning.${form.values?.country}`, {
      defaultValue: '',
    });
  }, [translate, form.values?.country]);

  const agreementNumberWarning = useMemo(() => {
    if (form.values?.country === CountryCode.BE && language === 'nl') {
      return translate('mt.breeder.onboarding.agreementNumber.warning.BE');
    }
  }, [language, translate, form.values?.country]);

  const agreementNumberIsRequired = useMemo(() => {
    switch (form.values?.country) {
      case CountryCode.NL:
        return true;
      case CountryCode.BE:
        return true;
      case CountryCode.FR:
        return form.values?.level === '3';
      default: // DE + CH
        return false;
    }
  }, [form.values?.level, form.values?.country]);

  const hideAgreementNumberField = useMemo(() => {
    return form.values?.country === CountryCode.DE;
  }, [form.values?.country]);

  const breedingNameLabel = useMemo(() => {
    if (form.values?.country === CountryCode.FR) {
      return translate(`mt.breeder.onboarding.breedingName.${form.values?.country}`);
    }
    return translate('mt.breeder.onboarding.breedingName.BE');
  }, [translate, form.values?.country]);

  const showGermanFileFields = useMemo(() => {
    return form.values?.country === CountryCode.DE;
  }, [form.values?.country]);

  const showCertificationFileField = useMemo(() => {
    const country = form.values?.country ?? CountryCode.BE;
    const level = form.values?.level ?? '';

    return [CountryCode.FR, CountryCode.NL].includes(country) && ['3', '6'].includes(level);
  }, [form.values?.country, form.values?.level]);

  const steps: OnboardingStep[] = useMemo(() => {
    return [
      {
        component: <Step0 loading={updateProfileLoading} onStartClick={onStartClickHandler} />,
        key: 0,
      },
      { component: <Step1 />, key: 1 },
      {
        component: (
          <Step2
            agreementNumberWarning={agreementNumberWarning}
            agreementNumberIsRequired={agreementNumberIsRequired}
            hideAgreementNumberField={hideAgreementNumberField}
            breederTypeWarning={breederTypeWarning}
          />
        ),
        key: 2,
      },
      {
        component: (
          <Step3
            breedOptions={breedOptionsWithoutCrossbreeds}
            breedingNameLabel={breedingNameLabel}
          />
        ),
        key: 3,
      },
      {
        component: <Step4 />,
        key: 4,
      },
      { component: <Step5 />, key: 5 },
      {
        component: <Step6 />,
        key: 6,
      },
      { component: <Step7 />, key: 7 },
      { component: <Step8 />, key: 8 },
      { component: <Step9 />, key: 9 },
      { component: <Step10 />, key: 10 },
      { component: <Step11 log={log} />, key: 11 },
      {
        component: (
          <Step12
            showGermanFileFields={showGermanFileFields}
            showCertificationFileField={showCertificationFileField}
            log={log}
          />
        ),
        key: 12,
      },
      {
        component: (
          <Step13
            onEditClick={onEditClickHandler}
            hideAgreementNumberField={hideAgreementNumberField}
            breedingNameLabel={breedingNameLabel}
            showGermanFileFields={showGermanFileFields}
            showCertificationFileField={showCertificationFileField}
            breedOptions={breedOptionsWithoutCrossbreeds}
          />
        ),
        key: 13,
      },
    ] as OnboardingStep[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agreementNumberIsRequired,
    agreementNumberWarning,
    breedOptionsWithoutCrossbreeds,
    breederTypeWarning,
    breedingNameLabel,
    hideAgreementNumberField,
    onStartClickHandler,
    showCertificationFileField,
    showGermanFileFields,
    updateProfileLoading,
  ]);

  const stepLineProps = useMemo(() => {
    let stepLineCurrentStep = 0;
    if (currentStep < 6) {
      stepLineCurrentStep = 0;
    } else if (currentStep < 9) {
      stepLineCurrentStep = 1;
    } else if (currentStep < 11) {
      stepLineCurrentStep = 2;
    } else {
      stepLineCurrentStep = 3;
    }
    return {
      steps: [
        { title: translate('common.inputs.steps.aboutYou') },
        { title: translate('common.inputs.steps.breedingPractices') },
        { title: translate('common.inputs.steps.yourDogs') },
        { title: translate('common.inputs.steps.docAndPhoto') },
      ],
      currentStep: stepLineCurrentStep,
    };
  }, [currentStep, translate]);

  const onSubmitFormHandler = useCallback(async () => {
    form.setFieldTouched('dogsConsanguinity', true); // hack to handle this field's error display
    await form.submitForm();
    scrollToError(form.errors);
  }, [form]);

  const onBackClickHandler = useCallback(() => {
    let nextStep = currentStep - 1;
    // Skip step 2 for swiss users
    if (currentStep === 3 && form.values?.country === CountryCode.CH) {
      nextStep -= 1;
    }
    setStep(nextStep);
  }, [currentStep, form.values?.country]);

  const onFinishOnboardingClickHandler = useCallback(async () => {
    if (profile?.dogBreeder?.hasFinishedOnboarding === false) {
      await finishOnboarding();
    }
    navigateToRoute('mtLanding');
  }, [finishOnboarding, navigateToRoute, profile?.dogBreeder?.hasFinishedOnboarding]);

  const onCloseClickHandler = useCallback(() => {
    navigateToRoute('mtLanding');
  }, [navigateToRoute]);

  useWindowConfirmUnload({ formIsDirty: form?.dirty, formIsSubmitting: form?.isSubmitting });
  return (
    <FormikProvider value={form}>
      <MtBreederOnboardingPage
        loading={updateProfileLoading}
        showCertificationFileField={showCertificationFileField}
        steps={steps}
        currentStep={currentStep}
        onNextClick={onSubmitFormHandler}
        onStartClick={onStartClickHandler}
        onBackClick={onBackClickHandler}
        stepLineProps={stepLineProps}
        onEditClick={onEditClickHandler}
        breedOptions={breedOptionsWithoutCrossbreeds}
        onCloseClick={onCloseClickHandler}
        agreementNumberIsRequired={agreementNumberIsRequired}
        hideAgreementNumberField={hideAgreementNumberField}
        agreementNumberWarning={agreementNumberWarning}
        breederTypeWarning={breederTypeWarning}
        breedingNameLabel={breedingNameLabel}
        showGermanFileFields={showGermanFileFields}
        onFinishOnboardingClick={onFinishOnboardingClickHandler}
        log={log}
      />
    </FormikProvider>
  );
};
